import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { loadScript, loadStyle } from '@/lib/utils';
// Mesmas URLs da tela antiga (resources/views/backend/manage/mails/inbox.blade.php).
const CKEDITOR_JS = 'https://cdn.ckeditor.com/ckeditor5/43.2.0/ckeditor5.umd.js';
const CKEDITOR_CSS = 'https://cdn.ckeditor.com/ckeditor5/43.2.0/ckeditor5.css';
/**
 * Editor do corpo do e-mail (CKEditor 5 por CDN, com a mesma configuração da tela antiga).
 * Se o CDN falhar, vira um campo de texto simples para o envio continuar funcionando.
 */
export const RichEditor = React.forwardRef(function RichEditor({ id, placeholder, minHeight }, ref) {
    const holder = React.useRef(null);
    const editor = React.useRef(null);
    const [state, setState] = React.useState('loading');
    const [plain, setPlain] = React.useState('');
    React.useImperativeHandle(ref, () => ({
        getData: () => (editor.current ? editor.current.getData() : plain),
        clear: () => {
            var _a;
            (_a = editor.current) === null || _a === void 0 ? void 0 : _a.setData('');
            setPlain('');
        },
    }), [plain]);
    React.useEffect(() => {
        let cancelled = false;
        loadStyle(CKEDITOR_CSS);
        loadScript(CKEDITOR_JS)
            .then(() => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const CK = window.CKEDITOR;
            if (cancelled || !CK || !holder.current)
                return;
            const { AccessibilityHelp, AutoImage, AutoLink, Bold, BalloonToolbar, Base64UploadAdapter, ClassicEditor, Essentials, Font, FontSize, ImageBlock, ImageInsert, ImageInsertViaUrl, ImageResize, ImageToolbar, ImageUpload, Italic, Link, LinkImage, Paragraph, } = CK;
            return ClassicEditor.create(holder.current, {
                plugins: [
                    AccessibilityHelp,
                    AutoImage,
                    AutoLink,
                    Bold,
                    BalloonToolbar,
                    Base64UploadAdapter,
                    Essentials,
                    Font,
                    FontSize,
                    ImageBlock,
                    ImageInsert,
                    ImageInsertViaUrl,
                    ImageResize,
                    ImageToolbar,
                    ImageUpload,
                    Italic,
                    Link,
                    LinkImage,
                    Paragraph,
                ],
                balloonToolbar: ['bold', 'italic', '|', 'link'],
                toolbar: ['undo', 'redo', '|', 'bold', 'italic', '|', 'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor', 'link', 'insertImage'],
                fontSize: { options: [10, 12, 14, 'default', 18, 20, 22], supportAllValues: true },
                image: { toolbar: ['imageTextAlternative', '|', 'resizeImage'] },
                link: {
                    addTargetToExternalLinks: true,
                    defaultProtocol: 'https://',
                    decorators: { toggleDownloadable: { mode: 'manual', label: 'Downloadable', attributes: { download: 'file' } } },
                },
                placeholder,
            }).then((instance) => {
                if (cancelled) {
                    instance.destroy();
                    return;
                }
                editor.current = instance;
                setState('ready');
            });
        })
            .catch((error) => {
            console.error(error);
            if (!cancelled)
                setState('fallback');
        });
        return () => {
            var _a;
            cancelled = true;
            (_a = editor.current) === null || _a === void 0 ? void 0 : _a.destroy().catch(() => undefined);
            editor.current = null;
        };
    }, []);
    if (state === 'fallback') {
        return _jsx(Textarea, { id: id, value: plain, onChange: (e) => setPlain(e.target.value), placeholder: placeholder, style: { minHeight } });
    }
    return (_jsxs("div", { className: "mail-editor text-foreground [&_.ck-powered-by]:hidden [&_.ck-editor__editable]:text-[#1e1e1e]", style: { ['--mail-editor-min']: `${minHeight}px` }, children: [state === 'loading' ? _jsx(Skeleton, { className: "w-full rounded-md", style: { height: minHeight } }) : null, _jsx("style", { children: `.mail-editor .ck-editor__editable{min-height:var(--mail-editor-min)}` }), _jsx("div", { children: _jsx("div", { id: id, ref: holder }) })] }));
});
